import forge from 'apis/forge';
import { IconType } from 'components/Icon';
import AttachmentStrip from 'components/Widget/AttachmentStrip';
import IconButton, { IconButtonState } from 'components/Widget/Button/IconButton';
import SubmitButton, { SubmitButtonState } from 'components/Widget/Button/SubmitButton';
import MultilineInput from 'components/Widget/Input/MultilineInput';
import ThumbnailStrip from 'components/Widget/ThumbnailStrip';
import * as api from 'dragonmouse-shared/api/forge';
import {
	CHAT_COLUMN_GAP,
	ChatMode,
	ChatStatus,
	SceneChat,
	UserChatEntry,
} from 'dragonmouse-shared/interfaces';
import { interceptIfNoCredits } from 'economy';
import { useFileUploadCallbacks } from 'hooks/useFileUploadCallbacks';
import { useSceneChatQueue } from 'hooks/useSceneChatQueue';
import useToast from 'hooks/useToast';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { selectActiveScene, selectActiveStyle, selectFrames } from 'redux/db/reducers';
import { canEdit, chatHasFrames, getRightmostFrameX } from 'redux/db/util';
import {
	removeFromSelected,
	setCenterFramesAsChatGeneratesThem,
	setPendingChatInput,
	setSelected,
} from 'redux/mode/actions';
import { Pick } from 'redux/mode/classes';
import { LeftPanelTab, selectIsMobile } from 'redux/mode/reducers';
import { displayViewOnlyModal } from 'redux/mode/util';
import { RootReducer, selectSelectedFrames } from 'redux/reducers';
import store from 'redux/store';
import { useTheme } from 'styled-components';
import { cancelUpload, onUploadFileDialog } from 'utils';
import { useChatInputHandlers } from '../../hooks/useChatInputHandlers';
import { useChatInputValidation } from '../../hooks/useChatInputValidation';
import * as Base from '../styles';
import DsPickerWrapper from './DsPickerWrapper';
import DsStatusBanner from './DsStatusBanner';

const WIDTH_SHOW_DS = 170;
const WIDTH_SHOW_CHAT_MODE = 140;

interface SceneChatInputProps {
	chat: SceneChat;
}

const SceneChatInput = ({ chat }: SceneChatInputProps) => {
	const dispatch = useDispatch();
	const theme = useTheme();
	const { message: toastMessage, isVisible: isToastVisible, showToast } = useToast();
	const {
		scene,
		frames,
		activeSceneId,
		pendingChatInput,
		activeStyle,
		selectedFrames,
		isMobile,
		chatPanelWidth,
	} = useSelector((r: RootReducer) => {
		return {
			scene: selectActiveScene(r.db, r.mode.activeSceneId),
			// include deleted frames because the chat can show thumbnails of frames that have been deleted from the canvas
			frames: selectFrames(r.db, true),
			activeSceneId: r.mode.activeSceneId,
			pendingChatInput: r.mode.pendingChatInputs[chat.id],
			activeStyle: selectActiveStyle(r.db, r.mode.activeSceneId),
			selectedFrames: selectSelectedFrames(r),
			isMobile: selectIsMobile(r.mode),
			chatPanelWidth: r.mode.leftPanelWidths[LeftPanelTab.CHAT],
		};
	}, shallowEqual);
	const [prompt, setPrompt] = useState<string>(pendingChatInput?.message || '');
	const [isFocused, setIsFocused] = useState<boolean>(false);
	const inputRef = useRef<HTMLTextAreaElement>(null);

	useEffect(() => {
		if (!pendingChatInput) {
			dispatch(
				setPendingChatInput({
					chatId: chat.id,
					pendingChatInput: {
						frameIds: [],
						message: '',
						chatMode: ChatMode.DESIGN_AND_CHAT,
					},
				})
			);
		}
	}, [chat.id, pendingChatInput, dispatch]);

	const lastSubmitRef = useRef<number>(0);
	const stoppable = chat?.metadata?.status === ChatStatus.STOPPABLE;

	const queue = useSceneChatQueue(chat?.id ?? null);

	// Validation logic for whether input can be submitted
	const { canSubmit } = useChatInputValidation({
		prompt,
		loadingAttachments: pendingChatInput?.loadingAttachments,
	});

	//------------------------------------------------------------------------------------------------
	// HANDLERS
	//------------------------------------------------------------------------------------------------

	const onSubmit = async () => {
		if (Date.now() - lastSubmitRef.current < 500) return;
		lastSubmitRef.current = Date.now();

		if (!activeSceneId || !chat) return;
		if (!canSubmit) return;

		if (!canEdit(scene)) {
			displayViewOnlyModal();
			return;
		}

		if (interceptIfNoCredits({ kind: 'chatEdit', sceneId: activeSceneId })) {
			return;
		}

		dispatch(setCenterFramesAsChatGeneratesThem(true));

		// If the chat has yet to place any frames on the canvas, recompute its X position.
		// Handles the case where the user generates frames in the chat's dedicated
		// column before chatting — without this, the chat starts in a column that
		// overlaps existing frames.
		//
		// Wrapped in try/catch so a failed positioning PATCH doesn't block the
		// actual submit. The position is cosmetic; the message is the point.
		if (!chatHasFrames(chat)) {
			const rightmostFrameX = getRightmostFrameX();
			if (rightmostFrameX !== null) {
				const updateRequest: api.UpdateChatRequest = {
					update: {
						id: chat.id,
						x: Math.round(rightmostFrameX + CHAT_COLUMN_GAP),
					},
				};
				try {
					await forge.updateSceneChat(updateRequest);
				} catch (e) {
					console.error('Failed to update chat x-position before submit:', e);
				}
			}
		}

		const entry: UserChatEntry = {
			kind: 'user',
			message: prompt,
			sourceFrameIds: thumbnailFrameIds,
			attachments: pendingChatInput.attachments || [],
		};

		const chatDesignSystemMode = store.getState().mode.chatDesignSystemMode;
		// Submission goes through the chat action queue. Idle chat → fires
		// immediately; busy chat → waits in FIFO and fires when idle.
		const trimmedPrompt = prompt.trim();
		const firstAttachmentName = entry.attachments?.[0]?.filename;
		const frameFallback =
			thumbnailFrameIds.length > 0
				? `${thumbnailFrameIds.length} frame${thumbnailFrameIds.length === 1 ? '' : 's'}`
				: undefined;
		const label = (trimmedPrompt || firstAttachmentName || frameFallback || 'Message').slice(
			0,
			80
		);
		queue.enqueue({
			label,
			payload: {
				kind: 'addUserEntry',
				entry,
				chatMode: pendingChatInput.chatMode,
				style: activeStyle,
				...(chatDesignSystemMode && { mode: chatDesignSystemMode }),
			},
		});

		// clear selected frames
		// the selected frames were part of thumbnailFrameIds, which have now been sent to the LLM
		// so let's deselect them because the user doesn't expect to include them in the next chat input
		dispatch(setSelected([]));
		setPrompt('');

		dispatch(
			setPendingChatInput({
				chatId: chat.id,
				pendingChatInput: {
					...pendingChatInput,
					frameIds: [],
					message: '',
					attachments: [],
				},
			})
		);
	};

	const onStop = async () => {
		if (!activeSceneId || !chat) return;

		await forge.updateSceneChat({
			update: {
				id: chat.id,
				metadata: {
					status: ChatStatus.PROCESSING,
					statusMessage: 'Stopping...',
				},
			},
		});
		const req: api.StopSceneChatGenerationRequest = {
			chatId: chat.id,
		};
		await forge.stopSceneChatGeneration.withToast()(req);
	};

	// Callback for updating prompt - handles both local state and Redux
	const onUpdatePrompt = useCallback(
		(prompt: string) => {
			setPrompt(prompt);
			dispatch(
				setPendingChatInput({
					chatId: chat.id,
					pendingChatInput: {
						...pendingChatInput,
						message: prompt,
					},
				})
			);
		},
		[setPrompt, dispatch, chat.id, pendingChatInput]
	);

	// File upload callbacks using Redux dispatch
	const fileCallbacks = useFileUploadCallbacks({
		type: 'scene',
		chatId: chat.id,
	});

	// Shared input handlers
	const { onKeyDown, onPaste, onContextMenu } = useChatInputHandlers({
		prompt,
		inputRef,
		canSubmit,
		onSubmit,
		onUpdatePrompt,
		fileCallbacks,
		chatEntries: chat.entries,
		pendingAttachments: pendingChatInput?.attachments,
	});

	const onRemoveThumbnail = (frameId: string) => {
		if (pendingChatInput.frameIds.includes(frameId)) {
			dispatch(
				setPendingChatInput({
					chatId: chat.id,
					pendingChatInput: {
						...pendingChatInput,
						frameIds: pendingChatInput.frameIds.filter((id) => id !== frameId),
					},
				})
			);
		} else if (selectedFrames.some((f) => f.id === frameId)) {
			dispatch(removeFromSelected(new Pick(frameId)));
		}
	};

	const onRemoveAttachment = (storagePath: string) => {
		dispatch(
			setPendingChatInput({
				chatId: chat.id,
				pendingChatInput: {
					...pendingChatInput,
					attachments: pendingChatInput.attachments?.filter((a) => a.storagePath !== storagePath),
				},
			})
		);
	};

	//------------------------------------------------------------------------------------------------
	// RENDERING
	//------------------------------------------------------------------------------------------------
	const thumbnailFrameIds = useMemo(() => {
		const pendingIds = pendingChatInput?.frameIds || [];
		const selectedIds = selectedFrames.map((f) => f.id);

		// Find newly selected frames that aren't already in pending chat input and sort them by X position (left to right)
		const sortedNewIds = selectedIds
			.filter((id) => !pendingIds.includes(id))
			.sort((a, b) => {
				const frameA = frames.find((f) => f.id === a);
				const frameB = frames.find((f) => f.id === b);
				if (!frameA || !frameB) return 0;
				// Sort by X first (left to right), then by Y descending (top to bottom)
				if (frameA.x !== frameB.x) return frameA.x - frameB.x;
				return frameA.y - frameB.y;
			});

		// Return existing pending frames followed by newly selected frames in sorted order
		return [...pendingIds, ...sortedNewIds];
	}, [pendingChatInput?.frameIds, selectedFrames, frames]);

	const renderAttachments = () => {
		const allAttachments = pendingChatInput?.attachments || [];
		const loading = pendingChatInput?.loadingAttachments;
		if (!allAttachments.length && !loading?.length) return null;
		return (
			<AttachmentStrip
				attachments={allAttachments}
				loadingAttachments={loading}
				onRemove={onRemoveAttachment}
				onCancel={cancelUpload}
			/>
		);
	};

	const renderAttachmentButton = () => {
		return (
			<IconButton
				icon={IconType.ADD}
				label={undefined}
				tooltip="Upload attachment"
				data-testid="chat-upload-attachment"
				onClick={() => {
					if (!canEdit(scene)) {
						displayViewOnlyModal();
						return;
					}
					onUploadFileDialog(fileCallbacks, true);
				}}
			/>
		);
	};

	const renderChatModeToggle = () => {
		const isChatOnly = pendingChatInput?.chatMode === ChatMode.CHAT_ONLY;
		return (
			<IconButton
				state={isChatOnly ? IconButtonState.SELECTED : IconButtonState.REGULAR}
				icon={isChatOnly ? IconType.CHAT : IconType.STYLE}
				tooltip={isChatOnly ? 'Turn off Chat-only Mode' : 'Turn on Chat-only Mode'}
				colors={{
					selected: theme.green[200],
					hover: isChatOnly ? theme.green[400] : theme.gray[200],
					active: 'transparent',
					text: isChatOnly ? theme.green[800] : theme.gray[1000],
				}}
				onClick={() => {
					const nextChatMode =
						pendingChatInput?.chatMode === ChatMode.DESIGN_AND_CHAT
							? ChatMode.CHAT_ONLY
							: ChatMode.DESIGN_AND_CHAT;
					dispatch(
						setPendingChatInput({
							chatId: chat.id,
							pendingChatInput: {
								...pendingChatInput,
								chatMode: nextChatMode,
							},
						})
					);

					if (isMobile) {
						showToast(
							nextChatMode === ChatMode.CHAT_ONLY ? 'Chat-only mode' : 'Chat + design mode'
						);
					}
				}}
			/>
		);
	};

	const renderActions = () => {
		const showAttachment = !isMobile;
		const showChatMode = chatPanelWidth > WIDTH_SHOW_CHAT_MODE;
		const showDs = chatPanelWidth > WIDTH_SHOW_DS;

		// Submit is always available — submissions go through the chat action
		// queue, so they enqueue when the chat is busy. When stoppable we
		// swap the icon to stop (clicking stops the current run, queue
		// preserved). The processing-only spinner is gone — QueueIndicator
		// above the input shows in-flight/queued state.
		return (
			<Base.Actions isMobile={isMobile}>
				{showAttachment && renderAttachmentButton()}
				{showChatMode && renderChatModeToggle()}
				{showDs && <DsPickerWrapper />}
				<Base.Spacer />
				<Base.LilWidthSpacer />
				<Base.SubmitButtonWrapper isMobile={isMobile}>
					<SubmitButton
						data-testid={stoppable ? 'chat-stop-button' : 'chat-submit-button'}
						state={
							stoppable || canSubmit ? SubmitButtonState.REGULAR : SubmitButtonState.DISABLED
						}
						colors={
							pendingChatInput?.chatMode === ChatMode.CHAT_ONLY
								? {
										disabled: theme.green[200],
										hover: theme.green[600],
										active: theme.green[800],
								  }
								: undefined
						}
						type={stoppable ? IconType.STOP : IconType.LINE_ARROW_UP}
						tooltip={stoppable ? 'Stop' : 'Submit'}
						tooltipPlacement="bottom-start"
						onClick={() => (stoppable ? onStop() : onSubmit())}
					/>
				</Base.SubmitButtonWrapper>
			</Base.Actions>
		);
	};

	return (
		<>
			{isMobile && toastMessage && (
				<Base.MobileToast isVisible={isToastVisible} role="status" aria-live="polite">
					{toastMessage}
				</Base.MobileToast>
			)}
			<Base.Base isMobile={isMobile}>
				<DsStatusBanner />
				<Base.Prompt isFocused={isFocused}>
					{renderAttachments()}
					<ThumbnailStrip frameIds={thumbnailFrameIds} onRemove={onRemoveThumbnail} selectOnClick />
					<Base.MultilineInputWrapper isMobile={isMobile} onPaste={onPaste}>
						<MultilineInput
							// this data attribute is part of a filthy hack to focus the chat input
							data-chat-input="true"
							data-testid="chat-input"
							ref={inputRef}
							value={prompt}
							onChange={(e) => onUpdatePrompt(e.currentTarget.value)}
							onKeyDown={onKeyDown}
							placeholder={'Design, critique, explore'}
							minLines={isMobile ? 1 : 4}
							renderBorder={false}
							variant={isMobile ? 'large' : 'medium'}
							onFocusChange={(isFocused) => {
								setIsFocused(isFocused);
							}}
							onContextMenu={onContextMenu}
						/>
					</Base.MultilineInputWrapper>
					{renderActions()}
				</Base.Prompt>
			</Base.Base>
		</>
	);
};

export default SceneChatInput;
